import * as React from 'react';
import moment from 'moment';
import { connectWithRouter } from 'helpers/connectWithRouter';
import { bindActionCreators, Dispatch } from 'redux';
import { hasPermission } from '../../helpers/Permission';
import { getOrgExpenseReports } from 'actions/ExpenseReports/Get';
import { editExpenseReport } from 'actions/ExpenseReports/Edit';
import RecordsTable, { Column, TabConfig, BulkAction } from './RecordsTable';
import EditRecordModal from './EditRecordModal';
import { ReduxState } from 'reducers/types';
import { PermissionTypes } from '../../constants';

const SYNC_STATUS_OPTIONS = [
  { value: 'ready', label: 'ready' },
  { value: 'synced', label: 'synced' },
  { value: 'failed', label: 'failed' },
  { value: 'notReady', label: 'notReady' },
  { value: 'processing', label: 'processing' },
];

const TABS: TabConfig[] = [
  { label: 'Open', filter: (r: any) => r.status !== 'paid' },
  { label: 'Paid', filter: (r: any) => r.status === 'paid' },
];

const COLUMNS: Column[] = [
  {
    label: 'Report',
    render: (r: any) => r.title ?? '',
  },
  {
    label: 'Reimbursement Date',
    render: (r: any) => {
      const raw = r.scheduledToReimburseDate?.[r.userId] ?? '';
      return moment(raw).isValid() ? moment(raw).format('MMM DD, YYYY') : '';
    },
  },
  {
    label: 'Amount',
    render: (r: any) => `$${(r.amounts?.totalSpent ?? 0).toFixed(2)}`,
  },
  {
    label: 'Status',
    render: (r: any) => r.status ?? '',
  },
  {
    label: 'Sync Status',
    render: (r: any) => r.syncToGlStatus ?? '',
  },
];

type Props = {
  organizationId: string;
  expenseReports: any[];
  isLoading: boolean;
  success: boolean | null;
  getOrgExpenseReports: Function;
  editExpenseReport: Function;
};

type State = {
  editModalOpen: boolean;
  selectedReport: any;
};

class OrgExpenseReportsTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      editModalOpen: false,
      selectedReport: null,
    };
  }

  componentDidMount() {
    if (this.props.organizationId) {
      this.props.getOrgExpenseReports(this.props.organizationId);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.success !== this.props.success && this.props.success === true) {
      this.props.getOrgExpenseReports(this.props.organizationId);
      this.setState({ editModalOpen: false, selectedReport: null });
    }
  }

  onClickRow = (report: any) => {
    if (hasPermission('expenseReport', PermissionTypes.update)) {
      this.setState({ editModalOpen: true, selectedReport: report });
    }
  };

  onCloseModal = () => {
    this.setState({ editModalOpen: false, selectedReport: null });
  };

  onSubmitEdit = (values: Record<string, string>) => {
    const { selectedReport } = this.state;
    if (selectedReport?.id) {
      this.props.editExpenseReport({
        reportIds: [selectedReport.id],
        syncToGlStatus: values.syncToGlStatus,
      });
    }
  };

  getDisplayFields = () => {
    const r = this.state.selectedReport;
    return [
      { label: 'Report', value: r?.title ?? '' },
      { label: 'Status', value: r?.status ?? '' },
      {
        label: 'Amount',
        value: `$${(r?.amounts?.totalSpent ?? 0).toFixed(2)}`,
      },
    ];
  };

  getEditFields = () => [
    {
      label: 'Sync to GL Status',
      key: 'syncToGlStatus',
      options: SYNC_STATUS_OPTIONS,
    },
  ];

  getInitialValues = () => ({
    syncToGlStatus: this.state.selectedReport?.syncToGlStatus ?? '',
  });

  onBulkUpdateSyncStatus = (selectedIds: string[], status: string) => {
    this.props.editExpenseReport({
      reportIds: selectedIds,
      syncToGlStatus: status,
    });
  };

  getBulkActions = (): BulkAction[] => {
    return SYNC_STATUS_OPTIONS.map((opt) => ({
      label: `Set ${opt.label}`,
      onClick: (ids: string[]) => this.onBulkUpdateSyncStatus(ids, opt.value),
    }));
  };

  render() {
    const canEdit = hasPermission('expenseReport', PermissionTypes.update);

    return (
      <>
        <RecordsTable
          columns={COLUMNS}
          records={this.props.expenseReports}
          tabs={TABS}
          isLoading={this.props.isLoading}
          onClickRow={canEdit ? this.onClickRow : undefined}
          keyField="id"
          selectable={canEdit}
          bulkActions={canEdit ? this.getBulkActions() : undefined}
        />

        <EditRecordModal
          open={this.state.editModalOpen}
          title="Edit Expense Report"
          displayFields={this.getDisplayFields()}
          editFields={this.getEditFields()}
          initialValues={this.getInitialValues()}
          onClose={this.onCloseModal}
          onSubmit={this.onSubmitEdit}
        />
      </>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    expenseReports: state.orgExpenseReports.expenseReports,
    isLoading: state.orgExpenseReports.isLoading,
    success: state.orgExpenseReports.success,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      getOrgExpenseReports,
      editExpenseReport,
    },
    dispatch,
  );
}

export default connectWithRouter(
  mapStateToProps,
  mapDispatchToProps,
)(OrgExpenseReportsTable);
